import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Form, 
  Input, 
  Button, 
  Alert, 
  Typography,
  Row,
  Col,
  Grid,
  Space,
  message,
  Card,
  Divider,
  InputNumber,
  Select,
  Checkbox,
  Collapse
} from 'antd';
import { 
  ArrowLeftOutlined,
  FileTextOutlined,
  UserOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useClientAdmin } from '../../context/ClientAdminContext';
import { checkAndRedirectToPayment } from '../../utils/paymentLinkHandler';
import { API_ORIGIN } from '../../config';

const { Content, Header } = Layout;
const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const GhannejjaGodda = () => {
  const navigate = useNavigate();
  const { clientAdmin } = useClientAdmin();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [localities, setLocalities] = useState([]);
  const [age, setAge] = useState(null);
  const [showGuardianFields, setShowGuardianFields] = useState(false);
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);

  const screens = Grid.useBreakpoint();
  const isSmall = !screens.md;
  const formFontColour = '#FFAC00';

  const formId = 7; // Għannejja Ġodda form ID
  
  // Get specific activity status
  const [isFormActive, setIsFormActive] = useState(true); // Default to true to show form initially

  // Array of logo paths
  const logos = '/images/Ghannejja/festghana_logo-04.png';

  // Fetch form activity status on component mount
  useEffect(() => {
    const fetchFormStatus = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/clientadmin/specificForm?id=${formId}`, {
          method: 'GET',
        });
        const data = await response.json();
        
        if (response.ok && data.success) {
          // Backend returns 0 or 1 (tinyint) - convert to boolean
          const isActive = data.activeForm === 1 || data.isActive === 1;
          setIsFormActive(isActive);
        } else {
          setIsFormActive(false); // Disable form if fetch fails
        }
      } catch (error) {
        setIsFormActive(false); // Disable form on error
      }
    };

    fetchFormStatus();
  }, []);

  // Fetch localities on component mount
  useEffect(() => {
    fetchLocalities();
  }, []);

  // Watch age field to show/hide guardian fields
  useEffect(() => {
    if (age !== null && age < 16) {
      setShowGuardianFields(true);
    } else {
      setShowGuardianFields(false);
      // Clear guardian fields if age is 16 or above
      if (age >= 16) {
        form.setFieldsValue({
          guardianFName: undefined,
          guardianLName: undefined,
          guardianTelNo: undefined,
          guardianEmail: undefined,
          guardianIdCard: undefined
        });
      }
    }
  }, [age, form]);

  const fetchLocalities = async () => {
    try {
      const response = await fetch(`${API_ORIGIN}/api/ghannejja/localities`);
      const data = await response.json();
      if (data.success) {
        setLocalities(data.localities || []);
      }
    } catch (error) {
      // silent fail
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const payload = {
        fName: values.fName,
        lName: values.lName,
        telNo: values.telNo,
        email: values.email,
        address: values.address,
        locality: values.locality,
        idCard: values.idCard,
        age: values.age,
        interest: values.interest,
        option1: values.options?.includes('familiar') ? 1 : 0,
        option2: values.options?.includes('other_genres') ? 1 : 0,
        option3: values.options?.includes('no_singing') ? 1 : 0
      };

      // Add guardian fields if age < 16
      if (values.age < 16) {
        payload.guardianFName = values.guardianFName;
        payload.guardianLName = values.guardianLName;
        payload.guardianTelNo = values.guardianTelNo;
        payload.guardianEmail = values.guardianEmail;
        payload.guardianIdCard = values.guardianIdCard;
      }

      const response = await fetch(`${API_ORIGIN}/api/ghannejja/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        message.success('Application submitted successfully!');
        form.resetFields();
        setAge(null);
        setShowGuardianFields(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Check if payment link exists and redirect
        const redirected = await checkAndRedirectToPayment('Għannejja Ġodda');
        if (!redirected) {
          // No payment link found, user stays on page with success message
        }
      } else {
        throw new Error(data.error || data.message || 'Submission failed');
      }
    } catch (error) {
      setError(error.message || 'An error occurred while submitting the application');
      message.error('Failed to submit application');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        /* Smooth transitions for buttons */
        .ant-btn {
          transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease, color 180ms ease;
        }

        /* Hover colour for all non-primary buttons */
        .ant-btn:not(.ghannejja-primary):hover {
          background-color: ${formFontColour} !important;
          border-color: ${formFontColour} !important;
          color: #fff !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 20px rgba(255,172,0,0.12) !important;
        }

        /* Primary submit button base style */
        .ghannejja-primary {
          background-color: ${formFontColour} !important;
          border-color: ${formFontColour} !important;
          color: #fff !important;
          transform: translateY(0);
          box-shadow: 0 6px 18px rgba(24,144,255,0.08);
        }

        /* Primary submit hover animation */
        .ghannejja-primary:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 14px 36px rgba(255,172,0,0.22) !important;
        }

        .ghannejja-primary:active {
          transform: translateY(-1px) !important;
        }

        /* Logo carousel RGB effect */
        .logo-carousel {
          width: 250px;
          height: 180px;
          transition: all 0.3s ease-in-out;
          animation: rgbPulse 3s infinite ease-in-out;
          filter: drop-shadow(0 0 20px rgba(255, 172, 0, 0.5));
        }

        @media (max-width: 768px) {
          .logo-carousel {
            width: 180px;
            height: 80px;
          }
        }
      `}</style>
      <Layout style={{ minHeight: '100vh', background: '#fafafa', overflowX: 'hidden' }}>
      <Header style={{ 
        padding: '12px 16px', 
        background: '#fff', 
        borderBottom: '1px solid #f0f0f0',
        height: 'auto',
        overflowX: 'hidden'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ 
            flex: '0 0 auto',
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingRight: isSmall ? 8 : 0
          }}>
            <img
              src="/images/FM_Logo.png"
              alt="Festivals Malta"
              onClick={() => navigate('/')}
              style={{
                height: isSmall ? '32px' : '36px',
                width: 'auto',
                maxWidth: '160px',
                cursor: 'pointer',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </Header>

      <Content style={{ padding: 'clamp(20px, 5vw, 48px)', overflowX: 'hidden' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: isSmall ? '0 12px' : '0 20px', width: '100%', boxSizing: 'border-box' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: isSmall ? '16px' : '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: isSmall ? '12px' : '24px' }}>
                <img 
                  src={logos}
                  alt="Għannejja Ġodda Logo" 
                  className="logo-carousel"
                  style={{ 
                    height: isSmall ? '80px' : '463px',
                    width: isSmall ? '180px' : '1125px',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              </div>
              <Title level={1} style={{ 
                color: '#222', 
                letterSpacing: '1px', 
                marginBottom: '8px', 
                fontSize: isSmall ? '24px' : '36px' 
              }}>
                Għannejja Ġodda 2026
              </Title>
              <Paragraph style={{ color: '#555', fontSize: isSmall ? '14px' : '18px' }}>
                Formola Tal-Applikazzjoni
              </Paragraph>
            </div>

            {success && (
              <Alert
                message="Application Submitted Successfully!"
                description="Your application has been received. We will review it and get back to you soon."
                type="success"
                showIcon
                closable
                onClose={() => setSuccess(false)}
              />
            )}

            {error && (
              <Alert
                message="Submission Error"
                description={error}
                type="error"
                showIcon
                closable
                onClose={() => setError('')}
              />
            )}

            {!isFormActive && (
              <Alert
                message="Form Currently Unavailable"
                description="This application form is currently closed. Please check back later or contact us for more information."
                type="warning"
                showIcon
                style={{ marginBottom: '24px' }}
              />
            )}

            <Collapse 
              defaultActiveKey={['1']} 
              style={{ marginBottom: '24px' }}
              items={[
                {
                  key: '1',
                  label: <span style={{ fontWeight: 600, fontSize: '16px' }}>Informazzjoni dwar il-Proġett</span>,
                  children: (
                    <div style={{ lineHeight: '1.8' }}>
                      <Title level={5} style={{ color: formFontColour, marginTop: 0 }}>
                        Deskrizzjoni tal-Proġett:
                      </Title>
                      <Paragraph style={{ marginBottom: '20px' }}>
                        Il-proġett Għannejja Ġodda huwa opportunità biex titħarreġ fil-għana Malti m'għannejja u daqqaqa stabbiliti. Il-parteċipanti jirċievu tagħrif dwar il-forom tal-għana u jitgħallmu jgħannu fatt miktub minn wieħed mill-għannejja Maltin ċelebri bħal Mikiel Abela l-Bambinu jew Frans Baldacchino l-Budaj. Il-parteċipanti li jispikkaw waqt dan it-taħriġ se jkunu mistiedna jgħannu waqt Festgħana, festival bl-għeruq u x-xniexel li jsir f'Mejju fil-Ġonna tal-Argotti.
                      </Paragraph>

                      <Title level={5} style={{ color: formFontColour }}>
                        Dati u Ħinijiet:
                      </Title>
                      <Paragraph style={{ marginBottom: '12px' }}>
                        <strong>Mill-10am sa 4pm (b'waqfa għall-ikel flimkien):</strong><br />
                        17 ta' Jannar: Spazju Kreattiv
                      </Paragraph>
                      <Paragraph style={{ marginBottom: '12px' }}>
                        <strong>Mis-2pm sal-4pm:</strong><br />
                        31 ta' Jannar: Ħaż-Żebbuġ<br />
                        14 ta' Frar: Bormla<br />
                        21 ta' Frar: Żurrieq<br />
                        7 ta' Marzu: Tarxien<br />
                        21 ta' Marzu: Rabat<br />
                        11 ta' April: Tal-Qroqq
                      </Paragraph>
                      <Paragraph style={{ marginBottom: '20px' }}>
                        Il-parteċipanti huma meħtieġa jattendu l-ewwel sessjoni obbligatorja u jagħżlu liema mis-sessjonijiet l-oħra konvenjenti għalihom, basta jattendu minimu ta' 4 sessjonijiet.
                      </Paragraph>

                      <Alert
                        message="Rekwiżiti Importanti"
                        description={
                          <>
                            It-taħriġ huwa miftuh għal kulħadd, bejn l-età ta' 13 u 30 sena, ta' kull ġeneru.<br />
                            <strong>Importanti li min jieħu sehem jaf jaqra u jitkellem bil-Malti.</strong>
                          </>
                        }
                        type="info"
                        showIcon
                      />
                    </div>
                  )
                }
              ]}
            />

            <Card
              style={{ 
                borderRadius: isSmall ? '8px' : '12px', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0'
              }}
              styles={{ body: { padding: isSmall ? 16 : 40 } }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                scrollToFirstError
                requiredMark={false}
                disabled={!isFormActive}
              >
                <Title level={4} style={{ color: formFontColour, marginBottom: 16 }}>
                  <UserOutlined /> Applikazzjoni tal-Parteċipant
                </Title>
                <Divider style={{ margin: '16px 0 24px 0' }} />

                <Row gutter={isSmall ? 12 : 24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="fName"
                      label={<span style={{ color: '#000' }}>Isem</span>}
                      rules={[{ required: true, message: 'Jekk jogħġbok daħħal l-isem tiegħek' }]}
                    >
                      <Input placeholder="Daħħal l-isem" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="lName"
                      label={<span style={{ color: '#000' }}>Kunjom</span>}
                      rules={[{ required: true, message: 'Jekk jogħġbok daħħal il-kunjom tiegħek' }]}
                    >
                      <Input placeholder="Daħħal il-kunjom" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={isSmall ? 12 : 24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="email"
                      label={<span style={{ color: '#000' }}>Indirizz tal-Email</span>}
                      rules={[
                        { required: true, message: 'Jekk jogħġbok daħħal l-indirizz tal-email tiegħek' },
                        { type: 'email', message: 'Jekk jogħġbok daħħal indirizz tal-email validu' }
                      ]}
                    >
                      <Input placeholder="email@example.com" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="telNo"
                      label={<span style={{ color: '#000' }}>Numru tal-Mobawjl</span>}
                      rules={[{ required: true, message: 'Jekk jogħġbok daħħal in-numru tal-Mobawjl tiegħek' }]}
                    >
                      <Input placeholder="Daħħal in-numru tal-Mobawjl" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={isSmall ? 12 : 24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="idCard"
                      label={<span style={{ color: '#000' }}>Karta tal-Identità</span>}
                      rules={[{ required: true, message: 'Jekk jogħġbok daħħal in-numru tal-Karta tal-Identità tiegħek' }]}
                    >
                      <Input placeholder="Daħħal in-numru tal-Karta tal-Identità" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="age"
                      label={<span style={{ color: '#000' }}>Età</span>}
                      rules={[
                        { required: true, message: 'Jekk jogħġbok daħħal l-età tiegħek' },
                        { 
                          type: 'number', 
                          min: 13, 
                          max: 30, 
                          message: 'L-età trid tkun bejn 13 u 30' 
                        }
                      ]}
                    >
                      <InputNumber 
                        placeholder="Daħħal l-età" 
                        style={{ width: '100%' }}
                        onChange={(value) => setAge(value)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="address"
                  label={<span style={{ color: '#000' }}>Indirizz</span>}
                  rules={[{ required: true, message: 'Jekk jogħġbok daħħal l-indirizz tiegħek' }]}
                >
                  <TextArea rows={2} placeholder="Daħħal l-indirizz sħiħ tiegħek" />
                </Form.Item>

                <Form.Item
                  name="locality"
                  label={<span style={{ color: '#000' }}>Lokalità</span>}
                  rules={[{ required: true, message: 'Jekk jogħġbok agħżel il-lokalità tiegħek' }]}
                >
                  <Select 
                    placeholder="Agħżel il-lokalità"
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {localities.map(loc => (
                      <Option key={loc.id} value={loc.id}>
                        {loc.locality}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Guardian Information (shown only if age < 16) */}
                {showGuardianFields && (
                  <>
                    <Title level={4} style={{ color: formFontColour, marginTop: '32px', marginBottom: 16 }}>
                      <TeamOutlined /> Informazzjoni tal-Gwardjan/Ġenitur
                    </Title>
                    <Divider style={{ margin: '16px 0 24px 0' }} />
                    <Alert
                      message="Informazzjoni tal-Gwardjan/Ġenitur Meħtieġa"
                      description="Peress li l-applikant huwa taħt l-età ta' 16-il sena, l-informazzjoni tal-gwardjan/ġenitur hija meħtieġa."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Row gutter={isSmall ? 12 : 24}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="guardianFName"
                          label={<span style={{ color: '#000' }}>Isem tal-Gwardjan/Ġenitur</span>}
                          rules={[{ required: true, message: 'Jekk jogħġbok daħħal l-isem tal-gwardjan/ġenitur' }]}
                        >
                          <Input placeholder="Daħħal l-isem tal-gwardjan/ġenitur" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="guardianLName"
                          label={<span style={{ color: '#000' }}>Kunjom tal-Gwardjan/Ġenitur</span>}
                          rules={[{ required: true, message: 'Jekk jogħġbok daħħal il-kunjom tal-gwardjan/ġenitur' }]}
                        >
                          <Input placeholder="Daħħal il-kunjom tal-gwardjan/ġenitur" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={isSmall ? 12 : 24}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="guardianEmail"
                          label={<span style={{ color: '#000' }}>Email tal-Gwardjan/Ġenitur</span>}
                          rules={[
                            { required: true, message: 'Jekk jogħġbok daħħal l-email tal-gwardjan/ġenitur' },
                            { type: 'email', message: 'Jekk jogħġbok daħħal email valida' }
                          ]}
                        >
                          <Input placeholder="email@example.com" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="guardianTelNo"
                          label={<span style={{ color: '#000' }}>Telefon tal-Gwardjan/Ġenitur</span>}
                          rules={[{ required: true, message: 'Jekk jogħġbok daħħal it-telefon tal-gwardjan/ġenitur' }]}
                        >
                          <Input placeholder="Daħħal it-telefon tal-gwardjan/ġenitur" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="guardianIdCard"
                      label={<span style={{ color: '#000' }}>Numru tal-Karta tal-Identità tal-Gwardjan/Ġenitur</span>}
                      rules={[{ required: true, message: 'Jekk jogħġbok daħħal in-numru tal-karta tal-identità tal-gwardjan/ġenitur' }]}
                    >
                      <Input placeholder="Daħħal in-numru tal-karta tal-identità tal-gwardjan/ġenitur" />
                    </Form.Item>
                  </>
                )}

                <Title level={4} style={{ color: formFontColour, marginTop: '32px', marginBottom: 16 }}>
                  <FileTextOutlined /> Dettalji tal-Applikazzjoni
                </Title>
                <Divider style={{ margin: '16px 0 24px 0' }} />

                <Form.Item
                  name="interest"
                  label={<span style={{ color: '#000' }}>Ikteb żewġ sentenzi dwar x'ħajjrek tapplika biex titħarreġ fl-għana?</span>}
                  rules={[{ required: true, message: 'Jekk jogħġbok ikteb żewġ sentenzi dwar għaliex trid tipparteċipa' }]}
                >
                  <TextArea 
                    rows={4} 
                    placeholder="Ikteb għaliex trid tipparteċipa"
                  />
                </Form.Item>

                <Form.Item
                  name="options"
                  label={<span style={{ color: '#000' }}>Liema minn dawn japplikaw għalik (tista' timmarka aktar minn waħda):</span>}
                  rules={[{ required: true, message: 'Jekk jogħġbok agħżel mill-inqas għażla waħda' }]}
                >
                  <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Checkbox value="familiar">Jiena familjari mad-dinja tal-għana</Checkbox>
                    <Checkbox value="other_genres">Jiena nkanta ġeneri oħrajn imma nixtieq nitgħallem ngħanni</Checkbox>
                    <Checkbox value="no_singing">Jiena ma nkantax imma nixtieq nitgħallem ngħanni</Checkbox>
                  </Checkbox.Group>
                </Form.Item>

                <Divider style={{ margin: '32px 0' }} />

                <Alert
                  message="Ħlas tal-Applikazzjoni"
                  description="Biex tieħu sehem f’dan il-programm, insaqsukom biex tħallsu 5 Ewro waqt l-ewwel sessjoni fis-17 ta’ Jannar fi Spazju Kreattiv"
                  type="warning"
                  showIcon
                  style={{ marginBottom: '24px' }}
                />

                <Form.Item style={{ marginTop: '32px', textAlign: 'center' }}>
                  <Button
                    htmlType="submit"
                    loading={loading}
                    size="large"
                    className="ghannejja-primary"
                    style={{ minWidth: isSmall ? '100%' : '200px', width: isSmall ? '100%' : 'auto' }}
                  >
                    Issottometti l-Formola
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Space>
        </div>
      </Content>
    </Layout>
    </>
  );
};

export default GhannejjaGodda;
